import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import _throttle from 'lodash/throttle';

import type { ReactRefT, TRect, TSize } from '@src/types/misc';
import type {
  SnapEdges,
  TextContentAlignment
} from '@src/types/wysiwyg_editor';
import type { BackgroundCategoryType } from '@src/types/ContributionBackground';
import { ImageCacheProvider } from './image_cache';

type State = {
  activePage: string;
  activeToolboxTab: string | null;
  awsBucket: string;
  awsRegion: string;
  backgroundOptions: Array<BackgroundCategoryType>;
  contentType: 'designed_layouts' | 'cover_designer';
  defaultTextAlignment: TextContentAlignment;
  domScaleBasisDimensions: {
    height: number;
    width: number;
  };
  offsetParentRef: ReactRefT<HTMLDivElement>;
  pageIsDirty: boolean;
  productionRender?: boolean;
  renderableArea: TRect;
  renderableDomLocation: TRect | null | undefined;
  renderRect: TRect | null | undefined;
  snapEdges: SnapEdges;
  svgRef: ReactRefT<SVGSVGElement>;
  viewbox: TSize;
};

type Props = {
  awsBucket: string;
  awsRegion: string;
  activePage: string;
  backgroundOptions?: Array<BackgroundCategoryType>;
  children: React.ReactElement;
  contentType: 'designed_layouts' | 'cover_designer';
  defaultTextAlignment: TextContentAlignment;
  initialPage: string;
  pageIsDirty: boolean;
  productionRender?: boolean;
  renderableArea: TRect;
  snapEdges: SnapEdges;
};

interface Dispatch {
  closeToolbox: () => void;
  openToolboxTab: (tab: string) => void;
  toggleToolboxTab: (tab: string) => void;
}

const viewbox = { height: 10000, width: 10000 };
export const domScaleBasisDimensions = { height: 646, width: 646 };

const defaultStateValue = {
  activePage: '',
  activeToolboxTab: null,
  awsBucket: '',
  awsRegion: '',
  backgroundOptions: [],
  contentType: 'unknown',
  defaultTextAlignment: 'left',
  domScaleBasisDimensions,
  offsetParentRef: null,
  pageIsDirty: false,
  renderableDomLocation: null,
  renderRect: null,
  renderableArea: { height: 0, width: 0, x: 0, y: 0 },
  snapEdges: { top: 0, bottom: 0, left: 0, right: 0 },
  svgRef: null,
  viewbox
};
const defaultDispatchValue = {
  closeToolbox() {},
  openToolboxTab() {},
  toggleToolboxTab() {}
};

export const WysiwygEditorStateContext =
  createContext<State>(defaultStateValue);
export const WysiwygEditorDispatchContext =
  createContext<Dispatch>(defaultDispatchValue);

export const WysiwygEditorProvider = (props: Props) => {
  const renderableViewboxLocation = props.renderableArea;
  const svgRef = useRef<SVGSVGElement | null | undefined>(null);
  const offsetParentRef = useRef<HTMLDivElement | null | undefined>(null);
  const [renderRect, setRenderRect] = useState<TRect | null | undefined>(null);
  const [renderableDomLocation, setRenderableDomLocation] = useState<
    TRect | null | undefined
  >(null);
  const [activeToolboxTab, changeToolboxTab] = useState<string>(null);

  const closeToolbox = useCallback(() => {
    changeToolboxTab(null);
  }, []);

  const toggleToolboxTab = useCallback(
    (tab: string) => {
      if (tab === activeToolboxTab) return closeToolbox();

      changeToolboxTab(tab);
    },
    [activeToolboxTab, closeToolbox]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const resizeObserver = new window.ResizeObserver(
      _throttle(function (_entries) {
        if (!offsetParentRef.current) return;

        const newRect = offsetParentRef.current?.getBoundingClientRect();
        const newValue = {
          height: newRect.height,
          width: newRect.width,
          x: newRect.left,
          y: newRect.top
        };

        setRenderRect(newValue);
        setRenderableDomLocation({
          height: renderableViewboxLocation.height * newValue.height,
          width: renderableViewboxLocation.width * newValue.width,
          x: renderableViewboxLocation.x * newValue.width,
          y: renderableViewboxLocation.y * newValue.height
        });
      }, 32)
    );

    resizeObserver.observe(offsetParentRef.current);

    return () => resizeObserver.disconnect();
  }, [renderableViewboxLocation]);

  return (
    <ImageCacheProvider>
      <WysiwygEditorDispatchContext.Provider
        value={{
          closeToolbox,
          openToolboxTab: changeToolboxTab,
          toggleToolboxTab
        }}
      >
        <WysiwygEditorStateContext.Provider
          value={{
            activePage: props.activePage,
            activeToolboxTab,
            awsBucket: props.awsBucket,
            awsRegion: props.awsRegion,
            contentType: props.contentType,
            backgroundOptions: props.backgroundOptions || [],
            defaultTextAlignment: props.defaultTextAlignment,
            domScaleBasisDimensions,
            offsetParentRef,
            pageIsDirty: props.pageIsDirty,
            productionRender: props.productionRender,
            renderableArea: props.renderableArea,
            renderableDomLocation,
            renderRect,
            snapEdges: props.snapEdges,
            svgRef,
            viewbox
          }}
        >
          {props.children}
        </WysiwygEditorStateContext.Provider>
      </WysiwygEditorDispatchContext.Provider>
    </ImageCacheProvider>
  );
};
